import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, usd } from "@/lib/api";
import { Logo } from "@/components/Shell";
import { useFetch } from "@/components/common";

const SIDE_IMG = "https://images.unsplash.com/photo-1605792657660-596af9009e82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen bg-[#0B0E14] lg:grid-cols-2">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Link to="/" data-testid="auth-logo-link"><Logo /></Link>
        <div className="my-auto w-full max-w-md py-12">
          <h1 className="text-3xl font-semibold tracking-tight text-white">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-400">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
      <div className="relative hidden lg:block border-l border-[#1E2738]">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#10B981]">AI Nexora</p>
          <p className="mt-3 font-display text-2xl text-white leading-snug">Every deposit verified. Every return recorded. Every withdrawal reviewed.</p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="nx-label">{label}</label>
    <input className="nx-input" data-testid={testid} {...props} />
  </div>
);

const Err = ({ msg }) => msg ? <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400" data-testid="auth-error">{msg}</p> : null;

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(f.email, f.password);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your trading account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="login-email-input" />
        <Field label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="login-password-input" />
        <div className="flex justify-end"><Link to="/forgot-password" className="text-xs text-[#10B981] hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
        <Err msg={err} />
        <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="login-submit-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Sign in
        </button>
        <p className="text-center text-sm text-slate-400">New to AI Nexora? <Link to="/register" className="text-[#10B981] hover:underline" data-testid="go-register-link">Create an account</Link></p>
      </form>
    </AuthLayout>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { data: s } = useFetch("/public/settings");
  const [f, setF] = useState({ name: "", email: "", mobile: "", password: "", confirm_password: "", referral_code: params.get("ref") || "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.password.length < 8) return setErr("Password must be at least 8 characters");
    if (f.password !== f.confirm_password) return setErr("Passwords do not match");
    setBusy(true);
    try {
      const u = await register({ ...f, referral_code: f.referral_code || null });
      if (u.welcome_bonus_credited) toast.success(`Welcome! ${usd(u.welcome_bonus_amount)} bonus credited to your account`);
      nav("/dashboard");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Create your account" subtitle="Start trading with AI-managed plans">
      {s?.welcome_bonus_enabled && s.welcome_bonus_amount > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 p-4" data-testid="register-bonus-banner">
          <Gift className="mt-0.5 shrink-0 text-[#10B981]" size={18} />
          <div>
            <p className="text-sm font-medium text-white">Get a {usd(s.welcome_bonus_amount)} welcome bonus</p>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">{s.welcome_bonus_terms}</p>
          </div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Full name" required value={f.name} onChange={set("name")} testid="register-name-input" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" type="email" required value={f.email} onChange={set("email")} testid="register-email-input" />
          <Field label="Mobile number" type="tel" required value={f.mobile} onChange={set("mobile")} placeholder="+91 98765 43210" testid="register-mobile-input" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Password" type="password" required value={f.password} onChange={set("password")} testid="register-password-input" />
          <Field label="Confirm password" type="password" required value={f.confirm_password} onChange={set("confirm_password")} testid="register-confirm-password-input" />
        </div>
        <Field label="Referral code (optional)" value={f.referral_code} onChange={set("referral_code")} placeholder="e.g. REFAB12CD" testid="register-referral-input" />
        <Err msg={err} />
        <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="register-submit-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Create account
        </button>
        <p className="text-center text-sm text-slate-400">Already registered? <Link to="/login" className="text-[#10B981] hover:underline" data-testid="go-login-link">Sign in</Link></p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Reset password" subtitle="We'll email you a secure reset link">
      {msg ? (
        <div className="space-y-4">
          <p className="rounded-lg border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-3 text-sm text-[#10B981]" data-testid="forgot-success">{msg}</p>
          <Link to="/login" className="nx-btn-ghost w-full" data-testid="back-to-login">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} testid="forgot-email-input" />
          <Err msg={err} />
          <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="forgot-submit-button">
            {busy && <Loader2 size={16} className="animate-spin" />} Send reset link
          </button>
          <p className="text-center text-sm"><Link to="/login" className="text-[#10B981] hover:underline">Back to sign in</Link></p>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setErr("Passwords do not match");
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: f.password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Choose a new password" subtitle="Minimum 8 characters">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <Field label="New password" type="password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="reset-password-input" />
        <Field label="Confirm password" type="password" required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} testid="reset-confirm-input" />
        <Err msg={err} />
        <button className="nx-btn-primary w-full py-3" disabled={busy} data-testid="reset-submit-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Update password
        </button>
      </form>
    </AuthLayout>
  );
}
